import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { purgeVolatileCaches } from "@/lib/security";
import type {
  DashboardData,
  Divergencia,
  NotaSimplificada,
  NotaStatus,
  PipelinePhase,
  PipelineState,
} from "@/lib/fiscal-types";
import { consolidate } from "@/lib/fiscal-consolidation";

export interface Profile {
  id: string;
  nome: string;
  user_id?: string;
  is_default?: boolean;
  cor?: string;
  cnpj?: string;
  created_at?: string;
}

interface PhaseProgress {
  phase: PipelinePhase;
  current: number;
  total: number;
  batch?: number;
}

export interface UploadRecord {
  id: string;
  fileName: string;
  fileSize: number;
  totalNotas: number;
  totalDivergencias: number;
  duracaoMs: number;
  timestamp: string;
  status: "success" | "error";
  entidadeNome?: string;
  entidadeCnpj?: string;
}

interface FiscalStore {
  state: PipelineState;
  message: string;
  phase: PipelinePhase;
  progress: Record<PipelinePhase, PhaseProgress>;
  fileName: string | null;
  fileSize: number;
  duracaoMs: number;
  dashboard: DashboardData | null;
  notas: NotaSimplificada[];
  filteredNotas: NotaSimplificada[];
  divergencias: Divergencia[];
  error: string | null;
  filtroStatus: NotaStatus | null;
  uploadHistory: UploadRecord[];
  privacyMode: boolean;
  filtroPeriodo: { inicio: string; fim: string } | null;
  setFiltroStatus: (s: NotaStatus | null) => void;
  togglePrivacyMode: () => void;
  setFiltroPeriodo: (inicio: string, fim: string) => void;
  set: (p: Partial<FiscalStore>) => void;
  setProgress: (p: PhaseProgress) => void;
  addUploadRecord: (r: UploadRecord) => void;
  removeUploadRecord: (id: string) => Promise<void>;
  applyAutoCorrections: () => { corrigidas: number; restantes: number };
  reprocessarPorCnpj: (cnpj: string) => void;
  /**
   * LGPD Art. 18 — Eliminação. Destrói todos os dados fiscais em memória
   * e limpa caches voláteis (sessionStorage). Deve ser invocado no logout.
   */
  purgeAllData: () => void;
  // Multi-empresa
  perfis: Profile[];
  perfilAtivo: Profile | null;
  setPerfis: (perfis: Profile[]) => void;
  setPerfilAtivo: (perfil: Profile | null) => void;
  // Alertas
  customAlerts: any[];
  setCustomAlerts: (alerts: any[]) => void;
  // Isolamento Multi-usuário
  userId: string | null;
  setUserId: (userId: string | null) => void;
  // Status de Sincronização
  lastSyncTime: string | null;
  syncProgress: { current: number, total: number } | null;
}

const emptyProgress: Record<PipelinePhase, PhaseProgress> = {
  upload: { phase: "upload", current: 0, total: 0 },
  extract: { phase: "extract", current: 0, total: 0 },
  process: { phase: "process", current: 0, total: 0 },
  consolidate: { phase: "consolidate", current: 0, total: 0 },
};

const initial = {
  state: "idle" as PipelineState,
  message: "",
  phase: "upload" as PipelinePhase,
  progress: emptyProgress,
  fileName: null,
  fileSize: 0,
  duracaoMs: 0,
  dashboard: null,
  notas: [],
  filteredNotas: [],
  divergencias: [],
  error: null,
  filtroStatus: null as NotaStatus | null,
  uploadHistory: [] as UploadRecord[],
  privacyMode: false,
  filtroPeriodo: null as { inicio: string; fim: string } | null,
  // Multi-empresa
  perfis: [] as Profile[],
  perfilAtivo: null as Profile | null,
  // Alertas
  customAlerts: [],
  // Isolamento Multi-usuário
  userId: null,
  lastSyncTime: null,
  syncProgress: null as { current: number, total: number } | null,
};

export const useFiscalStore = create<FiscalStore>()(
  persist(
    (set, get) => ({
  ...initial,
  setFiltroStatus: (s) => set({ filtroStatus: s }),
  togglePrivacyMode: () => set((s) => ({ privacyMode: !s.privacyMode })),
  set: (p) => set(p),
  setProgress: (p) =>
    set((s) => ({
      phase: p.phase,
      progress: { ...s.progress, [p.phase]: p },
    })),
  addUploadRecord: (r) =>
    set((s) => ({ uploadHistory: [r, ...s.uploadHistory].slice(0, 50) })),
  removeUploadRecord: async (id) => {
    const { uploadHistory, userId } = get();
    if (!userId) return;
    
    try {
      const { db } = await import("@/lib/local/db");
      const { syncLocalDBToStore } = await import("@/lib/local/sync");
      
      // 1. Identifica chaves para limpar itens
      const notasToDelete = await db.notas.where("loteId").equals(id).toArray();
      const chaves = notasToDelete.map(n => n.chave);
      
      // 2. Deleção física no IndexedDB
      await db.notas.where("loteId").equals(id).delete();
      if (chaves.length > 0) {
        await db.itens.where("documentoChave").anyOf(chaves).delete();
      }
      
      // 3. Remove do log persistido
      set({ uploadHistory: uploadHistory.filter(h => h.id !== id) });
      
      // 4. Reindexação Global: Força a sincronização total para atualizar Dashboards e Listas
      await syncLocalDBToStore();
      
      console.log(`[store] Lote ${id} removido com sucesso.`);
    } catch (err) {
      console.error("[store] Erro ao remover lote:", err);
      throw err;
    }
  },
  applyAutoCorrections: () => {
    const { divergencias, notas, dashboard } = get();
    if (!dashboard) return { corrigidas: 0, restantes: 0 };

    const TOL = 0.01;
    const chavesComDiv = new Set(divergencias.map((d) => d.chave));
    let corrigidasCount = 0;

    const novasNotas = notas.map((n) => {
      if (!chavesComDiv.has(n.chave) && n.divergencia == null) return n;

      const declarado = n.valorDeclarado ?? n.valorOficial;
      let calculado = n.valorItens;
      let diff = Math.abs(calculado - declarado);

      if (diff > TOL) {
        calculado = declarado;
        diff = 0;
      }

      const valorFinal = declarado;
      corrigidasCount++;
      return {
        ...n,
        valorItens: calculado,
        valorOficial: valorFinal,
        divergencia: null,
        status: null,
      };
    });

    const totalRevenue = novasNotas.reduce(
      (s, n) => (n.notaStatus === "AUTORIZADA" || n.notaStatus === "CONTINGENCIA" ? s + (n.valorOficial || 0) : s),
      0
    );
    const risk = { alta: 0, media: 0, baixa: 0 };
    for (const n of novasNotas) {
      if (n.notaStatus !== "AUTORIZADA" && n.notaStatus !== "CONTINGENCIA") continue;
      if (n.divergencia === "ALTA") risk.alta++;
      else if (n.divergencia === "MEDIA") risk.media++;
      else if (n.divergencia === "BAIXA") risk.baixa++;
    }

    set({
      divergencias: [],
      notas: novasNotas,
      dashboard: {
        ...dashboard,
        stats: { ...dashboard.stats, totalRevenue },
        riskSummary: risk,
      },
    });

    return { corrigidas: corrigidasCount, restantes: 0 };
  },
  reprocessarPorCnpj: (targetCnpj) => {
    const { notas, filtroPeriodo, divergencias } = get();
    if (!notas.length) return;

    const cleanTarget = targetCnpj ? targetCnpj.replace(/\D/g, "") : "";
    
    // 1. Aplica filtros de Entidade e Período combinados
    const notasFiltradas = notas.filter(n => {
      // Filtro de Entidade (se houver um selecionado)
      const matchesEntity = !cleanTarget || 
        n.cnpjEmit.replace(/\D/g, "") === cleanTarget || 
        n.cnpjDest.replace(/\D/g, "") === cleanTarget;
      
      if (!matchesEntity) return false;

      // Filtro de Período
      if (filtroPeriodo) {
        const date = n.emissao.split("T")[0];
        if (date < filtroPeriodo.inicio || date > filtroPeriodo.fim) return false;
      }

      return true;
    });

    // 2. Tenta encontrar o nome real da empresa para o Dashboard
    let foundName = "Geral / Todas";
    if (cleanTarget) {
      for (const n of notas) {
        if (n.cnpjEmit.replace(/\D/g, "") === cleanTarget) { foundName = n.emitente; break; }
        if (n.cnpjDest.replace(/\D/g, "") === cleanTarget) { foundName = n.destinatario; break; }
      }
    }

    // 3. Recalcula as operações baseado na perspectiva da empresa selecionada
    const notasComOp = notasFiltradas.map(n => {
      if (!cleanTarget) return n;
      const isEmit = n.cnpjEmit.replace(/\D/g, "") === cleanTarget;
      const isDest = n.cnpjDest.replace(/\D/g, "") === cleanTarget;
      const tpNF = n.tpNF;
      const hasTpNF = tpNF === 0 || tpNF === 1;

      let tipoOperacao: "ENTRADA" | "SAIDA" | "INDEFINIDA" = "INDEFINIDA";
      if (hasTpNF) {
        if (isEmit) tipoOperacao = tpNF === 1 ? "SAIDA" : "ENTRADA";
        else if (isDest) tipoOperacao = tpNF === 1 ? "ENTRADA" : "SAIDA";
      } else {
        if (isEmit && !isDest) tipoOperacao = "SAIDA";
        else if (isDest && !isEmit) tipoOperacao = "ENTRADA";
        else {
          const cfop0 = parseInt(n.cfopPrincipal?.[0] ?? "0");
          if (cfop0 >= 1 && cfop0 <= 3) tipoOperacao = "ENTRADA";
          else if (cfop0 >= 5 && cfop0 <= 7) tipoOperacao = "SAIDA";
        }
      }
      return { ...n, tipoOperacao };
    });

    const newDashboard = consolidate(notasComOp, divergencias);

    set({
      filteredNotas: notasComOp,
      dashboard: {
        ...newDashboard,
        cnpjEmpresa: targetCnpj || undefined,
        nomeEmpresa: foundName
      }
    });
  },
  setFiltroPeriodo: (inicio, fim) => {
    set({ filtroPeriodo: { inicio, fim } });
    const { dashboard } = get();
    // Re-aplica a lógica de CNPJ (que agora engloba o período também)
    get().reprocessarPorCnpj(dashboard?.cnpjEmpresa || "");
  },
  reset: () => set({ ...initial, progress: { ...emptyProgress }, uploadHistory: get().uploadHistory }),
  purgeAllData: () => {
    set({ ...initial, progress: { ...emptyProgress } });
    purgeVolatileCaches();
  },
  setPerfis: (perfis) => set({ perfis }),
  setPerfilAtivo: (perfilAtivo) => set({ perfilAtivo }),
  setCustomAlerts: (customAlerts) => set({ customAlerts }),
  setUserId: (userId) => set({ userId }),
}), {
  name: "fiscal-app-storage",
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({
    uploadHistory: state.uploadHistory,
    privacyMode: state.privacyMode,
    perfis: state.perfis,
    perfilAtivo: state.perfilAtivo,
    customAlerts: state.customAlerts,
    userId: state.userId,
    filtroPeriodo: state.filtroPeriodo,
  }),
}));
